import { useState, useEffect } from 'react'
import queryString from 'query-string'
import Box from '@material-ui/core/Box'
import Stepper from '@material-ui/core/Stepper'
import Step from '@material-ui/core/Step'
import StepLabel from '@material-ui/core/StepLabel'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Paper from '@material-ui/core/Paper'
import Card from '@material-ui/core/Card'
import CardActionArea from '@material-ui/core/CardActionArea'
import LinearProgress from '@material-ui/core/LinearProgress'
import Link from '@material-ui/core/Link'
import { makeStyles } from '@material-ui/core/styles'
import { useAppSelector } from '~/store'
import { get, post } from '~/tools/request'
import AgentSettingsForm from './agent-settings-form'

const steps = ['add_shop', 'import_products', 'configure_settings', 'active_widget', 'completed']

const stepTitles = [
    gettext('Add a Shop'),
    gettext('Import Products'),
    gettext('Configure settings'),
    gettext('Completed'),
]

const useStyles = makeStyles((theme) => ({
    createNewStore: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 48px)',
    },
    createNewStoreCard: {
        paddingTop: 40,
        paddingBottom: 40,
        paddingLeft: 40,
        paddingRight: 40,
        width: 900,
        display: 'flex',
        flexDirection: 'column',
    },
    stepInstruction: {
        margin: '16px 0',
        textAlign: 'center',
    },
    channels: {
        display: 'flex',
        marginBottom: 50,
        marginTop: 30,
        justifyContent: 'center',
    },
    channelCard: {
        width: 200,
        height: 75,
        marginLeft: 10,
        marginRight: 10,
    },
    channelCardActionArea: {
        height: '100%',
        display: 'flex',
    },
    channelIcon: {
        width: '70%',
    },
    submitButton: {
        marginTop: 16,
    },

    settingField: {
        display: 'flex',
        marginBottom: 10,
    },
    settingLabel: {
        width: '50%',
        paddingRight: 20,
        paddingTop: 12,
        textAlign: 'right',
    },
    settingValue: {
        width: '50%',
        textAlign: 'left',
    },
    formControl: {
        textAlign: 'left',
    },
}))

export default function OnboardingWizard() {
    const classes = useStyles()

    const [activeStep, setActiveStep] = useState(0)
    const [skipped, setSkipped] = useState(new Set())

    const [onboardingInfo, setOnboardingInfo] = useState({})
    const channels = onboardingInfo.channels || []

    useEffect(() => {
        const queryParams = queryString.parse(window.location.search)
        if (queryParams.step) {
            setActiveStep(steps.indexOf(queryParams.step))
        }
        fetchAgentOnboardingInfo()
    }, [])

    useEffect(() => {
        const storeId = onboardingInfo.store?.id
        const currentStep = onboardingInfo.settings?.current_onboarding_step
        if (storeId && steps[activeStep] !== currentStep) {
            post(
                bulkUpdateSettingValueAPIURL
                    .replace('source_type', 'store')
                    .replace('source_id', storeId),
                {
                    settings: { 'agent.current_onboarding_step': steps[activeStep] },
                }
            )
        }
    }, [activeStep])

    const isStepOptional = (step) => {
        return false
    }

    const isStepSkipped = (step) => {
        return skipped.has(step)
    }

    const handleNext = () => {
        let newSkipped = skipped
        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values())
            newSkipped.delete(activeStep)
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1)
        setSkipped(newSkipped)
    }

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }

    const handleSkip = () => {
        if (!isStepOptional(activeStep)) {
            throw new Error("You can't skip a step that isn't optional.")
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1)
        setSkipped((prevSkipped) => {
            const newSkipped = new Set(prevSkipped.values())
            newSkipped.add(activeStep)
            return newSkipped
        })
    }

    const bulkUpdateSettingValueAPIURL = useAppSelector(
        (state) => state.initial.endpoints.bulkUpdateSettingValueAPIURL
    )
    const agentOnboardingAPIURL = useAppSelector(
        (state) => state.initial.endpoints.agentOnboardingAPIURL
    )

    const fetchAgentOnboardingInfo = async () => {
        const { ok, ...data } = await get(agentOnboardingAPIURL)
        if (ok) {
            setOnboardingInfo(data)
            if (data.settings) {
                if (data.settings['agent.current_onboarding_step']) {
                    setActiveStep(steps.indexOf(data.settings['agent.current_onboarding_step']))
                }
            }
        }
    }

    const handleChannelClick = (channel) => {
        const queryParams = queryString.parse(window.location.search)
        if (queryParams.oauth_url) {
            window.location.href = queryParams.oauth_url
        } else {
            handleNext()
        }
    }

    return (
        <div className={classes.createNewStore}>
            <Paper className={classes.createNewStoreCard}>
                <Box sx={{ width: '100%' }}>
                    <Stepper activeStep={activeStep}>
                        {stepTitles.map((label, index) => {
                            const stepProps = {}
                            const labelProps = {}
                            if (isStepOptional(index)) {
                                labelProps.optional = (
                                    <Typography variant="caption">Optional</Typography>
                                )
                            }
                            if (isStepSkipped(index)) {
                                stepProps.completed = false
                            }
                            return (
                                <Step key={label} {...stepProps}>
                                    <StepLabel {...labelProps}>{label}</StepLabel>
                                </Step>
                            )
                        })}
                    </Stepper>
                    {activeStep === stepTitles.length ? (
                        <div style={{ textAlign: 'center' }}>
                            <Typography style={{ margin: '40px 0' }}>
                                {gettext('All steps completed - you are finished')}
                            </Typography>

                            <Link
                                component="button"
                                variant="body2"
                                onClick={() => {
                                    console.info("I'm a button.")
                                }}
                            >
                                {gettext('Go to Shopify shop')}
                            </Link>

                            <Link
                                component="button"
                                variant="body2"
                                style={{ marginLeft: 10 }}
                                onClick={() => {
                                    window.location.href = '/home/'
                                }}
                            >
                                {gettext('Go to Home page')}
                            </Link>
                        </div>
                    ) : (
                        <>
                            {activeStep === 0 && (
                                <>
                                    <Typography className={classes.stepInstruction} variant="body1">
                                        {gettext('Select the channel you want to integrate:')}
                                    </Typography>
                                    <div className={classes.channels}>
                                        {channels.map((channel) => (
                                            <Card key={channel.id} className={classes.channelCard}>
                                                <CardActionArea
                                                    className={classes.channelCardActionArea}
                                                    onClick={() => handleChannelClick(channel)}
                                                >
                                                    <img
                                                        className={classes.channelIcon}
                                                        src={channel.channel_icon_url}
                                                    />
                                                </CardActionArea>
                                            </Card>
                                        ))}
                                    </div>
                                </>
                            )}

                            {activeStep === 1 && (
                                <div style={{ textAlign: 'center', padding: '30px 34px' }}>
                                    <LinearProgress color="secondary" />

                                    <Typography className={classes.stepInstruction} variant="body1">
                                        {gettext(
                                            'Loading your products. No need to wait, you can continue the setup process.'
                                        )}
                                    </Typography>
                                </div>
                            )}

                            {activeStep === 2 && (
                                <div style={{ textAlign: 'center' }}>
                                    <Typography className={classes.stepInstruction} variant="body1">
                                        {gettext('Configure agent settings')}
                                    </Typography>

                                    <AgentSettingsForm agentInfo={onboardingInfo} />
                                </div>
                            )}

                            {activeStep === 3 && (
                                <div style={{ textAlign: 'center' }}>
                                    <Typography style={{ margin: '40px 0' }}>
                                        {gettext(
                                            'To enable the widget on your store, please activate it in Shopify.'
                                        )}
                                    </Typography>

                                    <Link component="button" variant="body2" onClick={handleNext}>
                                        {gettext('Activate Widget')}
                                    </Link>
                                </div>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                                {activeStep > 0 && (
                                    <Button
                                        color="inherit"
                                        disabled={activeStep === 0}
                                        onClick={handleBack}
                                        sx={{ mr: 1 }}
                                    >
                                        Back
                                    </Button>
                                )}
                                <Box sx={{ flex: '1 1 auto' }} />

                                {isStepOptional(activeStep) && (
                                    <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                                        Skip
                                    </Button>
                                )}

                                {activeStep > 0 && activeStep < stepTitles.length - 1 && (
                                    <Button onClick={handleNext}>Next</Button>
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </Paper>
        </div>
    )
}
